import {
  ErrorHandler,
  NgZone,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { Router, provideRouter, withRouterConfig } from '@angular/router';
import { Configuration as AppManagementApiConfiguration, DeviceApplicationStateService } from '@era/appmanagement-sdk';
import {
  CancelledDeviceTaskService,
  Configuration as DeviceTaskManagementApiConfiguration,
  DeviceTaskService,
} from '@era/device-task-management-sdk';
import { Configuration as GatewayApiConfiguration, DeviceService as UnenrolledDeviceService } from '@era/gateway-sdk';
import { Configuration as InRoomApiConfiguration } from '@era/inroom-sdk';
import {
  BarService,
  DeviceActionsService,
  DeviceOperationsService,
  DevicesService,
  FloorsService,
  FrontDeskService,
  HousekeepingService,
  IntegrationIReckonHotelsAndUnitsService,
  IntegrationMDMDevicesService,
  IntegrationMDMPairingService,
  IntegrationMDMProxyService,
  KioskService,
  Configuration as LocationsApiConfiguration,
  LocationsService,
  RoomStatusService,
  RoomsService,
  StaffService,
  SyncCurrentlyConfiguredService,
  SyncService,
} from '@era/locations-sdk';
import {
  AvailableService,
  DeviceService,
  DevicesService as DevicesOsUpdateService,
  LocationsService as LocationOsUpdateService,
  Configuration as OsUpdatesApiConfiguration,
} from '@era/osupdates-sdk';
import { GroupsService, Configuration as SecurityApiConfiguration, UsersService } from '@era/security-sdk';
import {
  ApplicationSettingsLinkService,
  ApplicationSettingsService,
  ApplicationsConfigurationService,
  CategoryAirPlaySecurityConfigurationService,
  CategoryAppLockConfigurationService,
  CategoryApplicationConfigurationService,
  CategoryRestrictionsProfileConfigurationService,
  CategoryVppApplicationConfigurationService,
  CategoryWifiProfileConfigurationService,
  Configuration as SettingsApiConfiguration,
  VppApplicationsConfigurationService,
} from '@era/settings-sdk';
import { CombinedErrorHandler, FeatureGuard } from '@rocksoft/app';
import { MenuService } from '@rocksoft/cdk';
import { Configuration as MdmDepConfiguration, ServerTokenService } from '@rocksoft/mdm-dep-sdk';
import {
  AppleAirPlaySecurityProfilePresetsService,
  AppleApplicationsService,
  AppleDevicesService,
  AppleRestrictionsProfilePresetsService,
  AppleSoftwareLookupServiceService,
  AppleVppService,
  AppleWifiProfilePresetsService,
  CertificateService,
  DeviceNotesService,
  Configuration as MdmApiConfiguration,
  Configuration as MdmApiConfigurationV2,
  MdmCommandProgressService,
  MdmCommandService,
  DevicesService as MdmDevicesService,
  MdmVendorService,
  OSUpdatesService,
  VppTokenService,
} from '@rocksoft/mdm-sdk-v2';
import {
  LocationsHubService,
  LocationsMdmHubService,
  SIGNALR_ROUTE_RULES,
  SIGNALR_URL_SELECTOR,
} from '@rocksoft/shared';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration, replayIntegration } from '@sentry/angular';
import { AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';

import { LoggingService, MenuVisibilityService, ResolverErrorLoggerProvider } from '@rocksoft/app';
import { environment, extModules } from '@rocksoft/environment';
import { provideAuthorizationService } from '@rocksoft/shared/authorization';
import { MESSAGE_LOGGING_CONFIG, MessageLogger, MessageLoggingService } from '@rocksoft/shared/logging';
import { validateEnvironment } from './environments/environment.model';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PairingSignalR, createDeviceSignalRRules } from '@era/devices';
import { createInRoomSignalRRules } from '@era/inroom';
import {
  APPLE_DEVICE_PROFILE_CONFIGS,
  APPLE_PROFILE_PRESET_CONFIGS,
  ExportService,
  HttpRequestService,
  RouterStoreSelectors,
  ScheduledApplicationsRequestService,
  createAppleDeviceProfileConfigs,
  createAppleProfilePresetConfigs,
} from '@era/stores';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { OdataService } from '@methylium/odata';
import { ListService } from '@ml-ag-grid-lib';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideEffects } from '@ngrx/effects';
import { Store, provideStore } from '@ngrx/store';
import { withFormlyBootstrap } from '@ngx-formly/bootstrap';
import { FormlyModule, provideFormlyCore } from '@ngx-formly/core';
import {
  APP_ROUTES,
  AppComponent,
  DevicesDeactivateGuard,
  LocationDeactivateGuard,
  RoomSettingsDeactivateGuard,
  SecurityApiConfigFactory,
  appManagementApiConfigFactory,
  deviceTaskManagementApiConfigFactory,
  gatewayApiConfigFactory,
  inRoomApiConfigFactory,
  locationsApiConfigFactory,
  mdmApiConfigFactory,
  mdmApiConfigFactoryV2,
  mdmDepConfigFactory,
  osUpdatesApiConfigFactory,
  provideAppStores,
  registerIconsFactory,
  settingsApiConfigFactory,
} from '@rocksoft/app';
import {
  CheckboxComponent,
  InputFieldComponent,
  RadioComponent,
  SelectComponent,
  TextAreaComponent,
} from '@rocksoft/cdk';
import { ToastEffect, ToastLoggingEffect } from '@rocksoft/shared/toast';
import { PlotlyViaCDNModule } from 'angular-plotly.js';

PlotlyViaCDNModule.setPlotlyVersion('1.58.4');

if (environment.sentry?.enabled && environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.sentry.release,
    integrations: [browserTracingIntegration(), replayIntegration()],
    tracesSampleRate: environment.sentry.tracesSampleRate,
    replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
    sendClientReports: true,
  });
}

if (environment.production) {
  enableProdMode();
}

validateEnvironment(environment);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideStore(),
    provideEffects([ToastEffect, ToastLoggingEffect]),
    provideAppStores(),
    provideAuthorizationService(),

    // SignalR route rules — each feature provides its own rules
    //
    // We provide two small pieces via DI instead of letting the shared
    // SignalR module import feature-specific code directly:
    // - `SIGNALR_URL_SELECTOR`: the router URL selector is supplied from the
    //    app's store (`RouterStoreSelectors.selectUrl`) so the shared service
    //    doesn't need to import `@era/stores` (avoids feature coupling).
    // - `SIGNALR_ROUTE_RULES`: features register their rule factories with
    //    `multi: true` so the shared service can aggregate them without
    //    importing feature modules. This prevents circular dependency
    //    evaluation and keeps the shared infra feature-agnostic.
    { provide: SIGNALR_URL_SELECTOR, useValue: RouterStoreSelectors.selectUrl },
    {
      provide: SIGNALR_ROUTE_RULES,
      useFactory: (store: Store, zone: NgZone) => createDeviceSignalRRules(store, zone),
      deps: [Store, NgZone],
      multi: true,
    },
    {
      provide: SIGNALR_ROUTE_RULES,
      useFactory: (store: Store, zone: NgZone) => createInRoomSignalRRules(store, zone),
      deps: [Store, NgZone],
      multi: true,
    },
    // Instrumentation must be imported after importing Store
    extModules,
    provideFormlyCore(withFormlyBootstrap()),
    importProvidersFrom(
      AuthModule.forRoot({ config: environment.oidc }),
      PlotlyViaCDNModule,
      FormlyModule.forRoot({
        types: [
          { name: 'input-field', component: InputFieldComponent },
          { name: 'checkbox', component: CheckboxComponent },
          { name: 'radio', component: RadioComponent },
          { name: 'select', component: SelectComponent },
          { name: 'text-area', component: TextAreaComponent },
        ],
      }),
      NgbModule
    ),
    {
      provide: APPLE_DEVICE_PROFILE_CONFIGS,
      useFactory: createAppleDeviceProfileConfigs,
      deps: [AppleDevicesService, AppleSoftwareLookupServiceService],
    },
    {
      provide: APPLE_PROFILE_PRESET_CONFIGS,
      useFactory: createAppleProfilePresetConfigs,
      deps: [
        AppleAirPlaySecurityProfilePresetsService,
        AppleRestrictionsProfilePresetsService,
        AppleWifiProfilePresetsService,
      ],
    },
    MenuVisibilityService,
    {
      provide: MenuService,
      deps: [Router, MenuVisibilityService],
      useFactory: (router: Router, service: MenuVisibilityService) => new MenuService(service.getMenus(), router),
    },
    {
      provide: ErrorHandler,
      useClass: CombinedErrorHandler,
    },
    {
      provide: MdmApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: mdmApiConfigFactory,
    },
    {
      provide: MdmApiConfigurationV2,
      deps: [OidcSecurityService],
      useFactory: mdmApiConfigFactoryV2,
    },
    {
      provide: MdmDepConfiguration,
      deps: [OidcSecurityService],
      useFactory: mdmDepConfigFactory,
    },
    {
      provide: LocationsApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: locationsApiConfigFactory,
    },
    {
      provide: SecurityApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: SecurityApiConfigFactory,
    },
    {
      provide: SettingsApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: settingsApiConfigFactory,
    },
    {
      provide: InRoomApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: inRoomApiConfigFactory,
    },
    {
      provide: GatewayApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: gatewayApiConfigFactory,
    },
    {
      provide: OsUpdatesApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: osUpdatesApiConfigFactory,
    },
    {
      provide: DeviceTaskManagementApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: deviceTaskManagementApiConfigFactory,
    },
    LoggingService,
    {
      provide: MESSAGE_LOGGING_CONFIG,
      useValue: {
        sentryEnabled: environment.sentry?.enabled ?? false,
        logToAppInsights: environment.appInsights?.instrumentationKey ?? undefined,
      },
    },
    {
      provide: MessageLogger,
      useClass: MessageLoggingService,
    },
    {
      provide: AppManagementApiConfiguration,
      deps: [OidcSecurityService],
      useFactory: appManagementApiConfigFactory,
    },
    FaIconLibrary,
    {
      provide: FaIconLibrary,
      useFactory: registerIconsFactory,
    },
    PairingSignalR,
    provideAppInitializer(() => {
      const pairingSignalR = inject(PairingSignalR);
      pairingSignalR.initialize();
    }),
    provideServices(),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(APP_ROUTES, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
  ],
}).catch((err: unknown) => {
  console.error(err);
  if (environment.sentry?.enabled) {
    Sentry.captureException(err);
  }
});

export function provideServices() {
  return [
    OdataService,
    ResolverErrorLoggerProvider,
    AppleApplicationsService,
    AppleVppService,
    LocationsService,
    RoomsService,
    GroupsService,
    UsersService,
    FloorsService,
    RoomStatusService,
    IntegrationIReckonHotelsAndUnitsService,
    IntegrationMDMPairingService,
    DevicesService,
    IntegrationMDMDevicesService,
    AppleWifiProfilePresetsService,
    AppleRestrictionsProfilePresetsService,
    MdmCommandService,
    AppleDevicesService,
    AppleSoftwareLookupServiceService,
    FeatureGuard,
    LocationDeactivateGuard,
    DevicesDeactivateGuard,
    RoomSettingsDeactivateGuard,
    LocationsHubService,
    LocationsMdmHubService,
    IntegrationMDMProxyService,
    AppleAirPlaySecurityProfilePresetsService,
    AppleApplicationsService,
    AppleVppService,
    CategoryAppLockConfigurationService,
    CategoryApplicationConfigurationService,
    CategoryVppApplicationConfigurationService,
    CategoryAirPlaySecurityConfigurationService,
    CategoryRestrictionsProfileConfigurationService,
    CategoryWifiProfileConfigurationService,
    // Housekeeping
    HousekeepingService,
    // Staff
    StaffService,
    // Front Desk
    FrontDeskService,
    // Bar
    BarService,
    // Kiosk
    KioskService,
    ExportService,
    HttpRequestService,
    ScheduledApplicationsRequestService,
    DeviceService,
    DevicesOsUpdateService,
    OSUpdatesService,
    LocationOsUpdateService,
    DeviceActionsService,
    DeviceOperationsService,
    MdmCommandProgressService,
    MdmDevicesService,
    UnenrolledDeviceService,
    ListService,
    VppTokenService,
    CertificateService,
    ServerTokenService,
    AvailableService,
    DeviceNotesService,
    MdmVendorService,
    ApplicationsConfigurationService,
    DeviceTaskService,
    CancelledDeviceTaskService,
    SyncCurrentlyConfiguredService,
    SyncService,
    ApplicationSettingsLinkService,
    ApplicationSettingsService,
    VppApplicationsConfigurationService,
    DeviceApplicationStateService,
  ];
}
